<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="$t('auth.login.title')"
    :style="{ width: '500px', minWidth: '400px' }"
    :closable="true"
    :close-on-escape="true"
  >
    <p v-if="subtitle" class="login-subtitle">{{ subtitle }}</p>
    <form class="space-y-4" @submit.prevent="handleLogin">
      <div class="field">
        <label for="username" class="block text-sm font-medium mb-2">{{
          $t('auth.login.usernameLabel')
        }}</label>
        <InputText
          id="username"
          v-model="form.username"
          type="text"
          :placeholder="$t('auth.login.usernamePlaceholder')"
          :class="{ 'p-invalid': errors.username }"
          required
        />
        <small v-if="errors.username" class="p-error">{{
          errors.username
        }}</small>
      </div>

      <div class="field">
        <label for="password" class="block text-sm font-medium mb-2">{{
          $t('auth.login.passwordLabel')
        }}</label>
        <div class="password-input-container">
          <InputText
            id="password"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            :placeholder="$t('auth.login.passwordPlaceholder')"
            :class="{ 'p-invalid': errors.password }"
            required
          />
          <Button
            :icon="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"
            class="password-toggle"
            severity="secondary"
            text
            @click="togglePassword"
          />
        </div>
        <small v-if="errors.password" class="p-error">{{
          errors.password
        }}</small>
      </div>

      <div v-if="authStore.error" class="p-error text-sm">
        {{ authStore.error }}
      </div>
    </form>

    <template #footer>
      <div class="flex justify-between w-full">
        <Button
          v-if="showRegisterButton"
          :label="$t('auth.register.title')"
          icon="pi pi-user-plus"
          class="p-button-outlined"
          @click="showRegister = true"
        />
        <div v-else />
        <div class="flex gap-2">
          <Button
            :label="$t('g.cancel')"
            icon="pi pi-times"
            class="p-button-text"
            @click="handleCancel"
          />
          <Button
            :label="$t('auth.login.loginButton')"
            icon="pi pi-check"
            :loading="authStore.isLoading"
            @click="handleLogin"
          />
        </div>
      </div>
    </template>

    <!-- Register Dialog -->
    <RegisterDialog
      v-if="showRegisterButton"
      v-model:visible="showRegister"
      @success="handleRegisterSuccess"
    />
  </Dialog>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import { computed, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useAuthStore } from '@/stores/authStore'

import RegisterDialog from './RegisterDialog.vue'

const router = useRouter()

interface Props {
  visible: boolean
  /** 自定义副标题，如 "请登录以访问 工作流" */
  subtitle?: string
  /** 是否显示注册按钮，精简模式下为 false */
  showRegisterButton?: boolean
  /** 登录成功后的重定向路径 */
  redirectPath?: string
}

interface Emits {
  (e: 'update:visible', value: boolean): void
  (e: 'success'): void
}

const props = withDefaults(defineProps<Props>(), { showRegisterButton: true })
const emit = defineEmits<Emits>()

const authStore = useAuthStore()
const showRegister = ref(false)
const showPassword = ref(false)

const form = reactive({
  username: '',
  password: ''
})

const errors = reactive({
  username: '',
  password: ''
})

const togglePassword = () => {
  showPassword.value = !showPassword.value
}

const validateForm = () => {
  errors.username = ''
  errors.password = ''

  if (!form.username.trim()) {
    errors.username = 'Username is required'
    return false
  }

  if (!form.password) {
    errors.password = 'Password is required'
    return false
  }

  return true
}

const handleLogin = async () => {
  if (!validateForm()) return

  // 立即重置加载状态，确保按钮可以显示加载动画
  authStore.resetLoadingState()

  const success = await authStore.login({
    username: form.username,
    password: form.password
  })

  if (success) {
    console.log('LoginDialog: Login successful, emitting success event')
    emit('success')
    // Wait a bit to ensure auth store state is updated
    await new Promise((resolve) => setTimeout(resolve, 100))

    // Check auth store state after login
    const currentState = {
      isAuthenticated: authStore.isAuthenticated,
      hasToken: !!authStore.getAccessToken(),
      hasUser: !!authStore.currentUser
    }
    console.log('LoginDialog: Auth store state after login:', currentState)

    // Don't close dialog immediately - let the auth dialog composable handle it
    // visible.value = false
    resetForm()

    // Redirect after successful login
    const redirectPath = props.redirectPath ?? '/'
    console.log(
      'LoginDialog: Redirecting after successful login to',
      redirectPath
    )
    await router.replace(redirectPath)
  }
}

const handleCancel = () => {
  visible.value = false
  resetForm()
  // Emit event to notify parent that dialog was closed without successful login
  window.dispatchEvent(new CustomEvent('login-dialog-close'))
}

const handleRegisterSuccess = () => {
  showRegister.value = false
  // Optionally auto-fill username from registration
}

const resetForm = () => {
  form.username = ''
  form.password = ''
  errors.username = ''
  errors.password = ''
}

const visible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value)
})

// Reset form when dialog opens
watch(visible, (newValue) => {
  if (newValue) {
    resetForm()
  }
})
</script>

<style scoped>
.login-subtitle {
  color: #ccc;
  margin: 0 0 1rem 0;
  font-size: 14px;
}

.field {
  margin-bottom: 1rem;
}

.field input {
  width: 100%;
  box-sizing: border-box;
  background: #2a2a2a;
  border: 1px solid #444;
  color: #fff;
}

.field input::placeholder {
  color: #888;
}

.field input:focus {
  outline: none;
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.password-input-container {
  position: relative;
  width: 100%;
}

.password-input-container input {
  width: 100%;
  padding-right: 45px; /* 为密码切换按钮留出空间 */
  box-sizing: border-box;
  background: #2a2a2a;
  border: 1px solid #444;
  color: #fff;
}

.password-input-container input::placeholder {
  color: #888;
}

.password-input-container input:focus {
  outline: none;
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  padding: 8px;
}

.password-toggle:hover {
  background-color: rgba(255, 107, 53, 0.1);
  border-radius: 4px;
}

/* 毛玻璃效果样式 */
.p-dialog {
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.p-dialog .p-dialog-content {
  background: rgba(26, 26, 26, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 107, 53, 0.2);
  border-radius: 12px;
}
</style>
